import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Download, Copy, Share2, Check, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { track, EVENTS } from '../lib/analytics';

/**
 * RfqTrackingShare
 *
 * Reusable QR-code + share-action card for an RFQ reference. Used on the
 * RFQ submission success screen and inline on the /track found view.
 *
 * Renders:
 *   • QR code (encodes /track?ref=<reference>)
 *   • Copy reference button
 *   • Copy tracking link button
 *   • Download QR button (PNG)
 *   • Share button (Web Share API on mobile, falls back to copy on desktop)
 *
 * Props:
 *   reference: string (required)       — RFQ-XXXXXXXX code
 *   compact:   boolean (default false) — denser layout for inline use
 *   surface:   'light' | 'dark'        — color palette
 */
const RfqTrackingShare = ({ reference, compact = false, surface = 'light' }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(null); // 'ref' | 'link' | null
  const generatedOnce = useRef(false);

  const trackingPath = `/track?ref=${encodeURIComponent(reference)}`;
  const trackingUrl = `${window.location.origin}${trackingPath}`;

  // Generate the QR code as a PNG data URL whenever reference changes.
  useEffect(() => {
    let cancelled = false;
    if (!reference) return;
    QRCode.toDataURL(trackingUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 480, // high-res so download stays crisp
      color: surface === 'dark'
        ? { dark: '#FFFFFF', light: '#0A0A0A' }
        : { dark: '#0A0A0A', light: '#FFFFFF' },
    })
      .then((url) => {
        if (cancelled) return;
        setQrDataUrl(url);
        if (!generatedOnce.current) {
          track(EVENTS.RFQ_QR_GENERATED, { reference_code: reference, surface });
          generatedOnce.current = true;
        }
      })
      .catch((e) => {
        console.error('[qr] generation failed', e);
      });
    return () => { cancelled = true; };
  }, [reference, trackingUrl, surface]);

  const handleCopy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
      if (kind === 'link') {
        track(EVENTS.RFQ_TRACK_LINK_COPIED, { reference_code: reference });
        toast.success('Tracking link copied to clipboard.');
      } else {
        toast.success('Reference code copied.');
      }
    } catch {
      toast.error('Could not copy. Long-press the field to copy manually.');
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `giwahs-${reference}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    track(EVENTS.RFQ_QR_DOWNLOADED, { reference_code: reference });
    toast.success('QR code downloaded.');
  };

  const handleShare = async () => {
    const payload = {
      title: `GIWAHS RFQ ${reference}`,
      text: `Track GIWAHS sourcing request ${reference}`,
      url: trackingUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(payload);
        track(EVENTS.RFQ_TRACK_LINK_SHARED, { reference_code: reference, channel: 'web-share-api' });
      } catch (e) {
        // User cancelled — silent
        if (e?.name !== 'AbortError') console.error('[share]', e);
      }
    } else {
      // Desktop fallback — copy and notify
      await handleCopy(trackingUrl, 'link');
      track(EVENTS.RFQ_TRACK_LINK_SHARED, { reference_code: reference, channel: 'clipboard-fallback' });
    }
  };

  const isDark = surface === 'dark';
  const cardClass = isDark ? 'border border-white/20 bg-[#0A0A0A] text-white' : 'border border-black bg-[#F5F5F0]';
  const btnPrimary = isDark
    ? 'border-white bg-white text-black hover:bg-transparent hover:text-white'
    : 'border-black bg-black text-white hover:bg-transparent hover:text-black';
  const btnGhost   = isDark
    ? 'border-white/40 text-white hover:bg-white hover:text-black'
    : 'border-black text-black hover:bg-black hover:text-white';
  const overlineClass = isDark ? 'text-white/60' : 'text-neutral-500';
  const subtleText = isDark ? 'text-white/70' : 'text-neutral-600';

  return (
    <div data-testid="rfq-tracking-share" className={`${cardClass} ${compact ? 'p-5' : 'p-6 md:p-7'}`}>
      <div className={`flex items-center gap-2 label-overline ${overlineClass}`}>
        <QrCode size={12} strokeWidth={1.5} /> Save your tracking link
      </div>
      {!compact && (
        <h3 className={`mt-2 font-display ${isDark ? 'text-white' : 'text-black'}`} style={{ fontSize: 'clamp(1.25rem,2.5vw,1.5rem)' }}>
          Track this RFQ anytime.
        </h3>
      )}
      <p className={`mt-2 text-xs md:text-sm ${subtleText}`}>
        Scan to reopen status on any device, or copy the link. No account needed — your reference code is the key.
      </p>

      <div className={`mt-5 grid items-start gap-5 ${compact ? '' : 'md:grid-cols-[auto_1fr]'}`}>
        {/* QR */}
        <div className={`mx-auto ${compact ? '' : 'md:mx-0'}`}>
          {qrDataUrl ? (
            <img
              data-testid="rfq-tracking-qr-image"
              src={qrDataUrl}
              alt={`QR code linking to ${trackingPath}`}
              width={compact ? 144 : 176}
              height={compact ? 144 : 176}
              className={`block ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} ${isDark ? 'border border-white/20' : 'border border-black'}`}
              style={{ imageRendering: 'pixelated' }}
            />
          ) : (
            <div
              data-testid="rfq-tracking-qr-placeholder"
              className={`grid h-[176px] w-[176px] place-items-center ${isDark ? 'border border-white/20 bg-white/5' : 'border border-neutral-300 bg-white'} animate-pulse`}
            >
              <QrCode size={28} strokeWidth={1} className={isDark ? 'text-white/30' : 'text-neutral-300'} />
            </div>
          )}
        </div>

        {/* Actions + ref */}
        <div className="flex flex-col gap-3">
          {/* Reference field */}
          <div>
            <span className={`label-overline ${overlineClass}`}>Reference</span>
            <div className={`mt-1 flex items-stretch border ${isDark ? 'border-white/30' : 'border-black'} bg-transparent`}>
              <code
                data-testid="rfq-tracking-share-ref"
                className={`flex-1 truncate bg-transparent px-3 py-2 font-mono-data text-sm tracking-wider ${isDark ? 'text-white' : 'text-black'}`}
              >
                {reference}
              </code>
              <button
                type="button"
                onClick={() => handleCopy(reference, 'ref')}
                data-testid="rfq-tracking-copy-ref"
                aria-label="Copy reference code"
                className={`inline-flex items-center gap-1.5 border-l ${isDark ? 'border-white/30 hover:bg-white hover:text-black' : 'border-black hover:bg-black hover:text-white'} px-3 text-[10px] font-medium uppercase tracking-wider`}
              >
                {copied === 'ref' ? <><Check size={12} strokeWidth={1.5} /> Copied</> : <><Copy size={12} strokeWidth={1.5} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleCopy(trackingUrl, 'link')}
              data-testid="rfq-tracking-copy-link"
              className={`inline-flex items-center gap-2 border ${btnGhost} px-3 py-2 text-xs font-medium uppercase tracking-wider`}
            >
              {copied === 'link' ? <><Check size={12} strokeWidth={1.5} /> Copied</> : <><Copy size={12} strokeWidth={1.5} /> Copy link</>}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!qrDataUrl}
              data-testid="rfq-tracking-download-qr"
              className={`inline-flex items-center gap-2 border ${btnGhost} px-3 py-2 text-xs font-medium uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Download size={12} strokeWidth={1.5} /> Download QR
            </button>
            <button
              type="button"
              onClick={handleShare}
              data-testid="rfq-tracking-share-btn"
              className={`inline-flex items-center gap-2 border ${btnPrimary} px-3 py-2 text-xs font-medium uppercase tracking-wider`}
            >
              <Share2 size={12} strokeWidth={1.5} /> Share
            </button>
          </div>

          <p className={`text-[10px] uppercase tracking-wider ${overlineClass}`}>
            Treat this link like a parcel-tracking code — anyone who has it can view status, but not your private details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RfqTrackingShare;
